/**
 * The sign-in button for a provider that has its own branding rules.
 *
 * Google and Microsoft both publish how their sign-in buttons must look — the
 * exact mark, the wording ("Sign in with Google", not "Log in with Google"),
 * and a minimum clear space around the logo. A generic "Continue to Gmail"
 * button is not just off-brand: to someone about to hand over mailbox access it
 * is the wrong signal entirely, because the recognisable mark is the thing that
 * tells them where they are going before they get there.
 *
 * The marks are inlined as SVG rather than fetched, so they render offline and
 * cannot be blocked by an ad blocker on a page that would then show a button
 * with no logo at all.
 */

import type { AuthMethod } from '@/core/types/api.types'

/** Google's four-colour "G", from their published sign-in assets. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58Z"
      />
    </svg>
  )
}

/** Microsoft's four-square mark. */
function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  )
}

const BRANDS = {
  google: { Mark: GoogleMark, label: 'Sign in with Google' },
  microsoft: { Mark: MicrosoftMark, label: 'Sign in with Microsoft' },
} as const

/** Whether this method should use a branded button rather than the generic one. */
export function brandFor(method: AuthMethod | null): keyof typeof BRANDS | null {
  const provider = method?.identity_provider
  return provider && provider in BRANDS ? provider : null
}

export function IdentityButton({
  brand,
  busy,
  disabled,
  onClick,
}: {
  brand: keyof typeof BRANDS
  busy: boolean
  disabled: boolean
  onClick: () => void
}) {
  const { Mark, label } = BRANDS[brand]
  return (
    <button
      type="button"
      className="identity-btn"
      data-brand={brand}
      onClick={onClick}
      disabled={disabled}
    >
      {busy ? <span className="spinner" /> : <Mark />}
      <span>{busy ? 'Waiting for approval…' : label}</span>
    </button>
  )
}
